"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, ChartBar as BarChart3, Building2, CircleCheck as CheckCircle2, MapPin, Search, TrendingUp, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Northampton() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-reveal", {
        y: 28
      });
      gsap.to(".gsap-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-reveal", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const marketInsights = [{
    icon: Search,
    title: "High Commercial Intent",
    description: "Northamptonshire buyers and decision-makers actively search for regional B2B services, logistics, and specialized trades with immediate buying intent.",
    metric: "72%",
    metricLabel: "Local searches result in direct contact or site visit"
  }, {
    icon: TrendingUp,
    title: "Geo-Targeted Keyword Opportunity",
    description: "Many local businesses rely solely on basic Google Business profiles, leaving high-value commercial keyword gaps open for strategic organic capture.",
    metric: "3.4x",
    metricLabel: "Average ROI when targeting long-tail regional terms"
  }, {
    icon: Building2,
    title: "Regional Enterprise Growth",
    description: "As part of the Golden Triangle logistics and growth corridor, Northampton businesses face increasing competition that requires robust technical SEO.",
    metric: "68%",
    metricLabel: "Market share dominated by top 3 organic search positions"
  }];
  const strategyPoints = ["Direct strategic audit of your current Northamptonshire search positioning", "Tailored geo-entity optimization for map packs and organic results", "High-converting landing page structure designed for Northampton searchers", "Transparent keyword tracking and clear monthly impact reporting", "Technical fixes to eliminate crawl blocks and improve indexing speed"];
  return <section id="northamptonsearchlandscape" data-nav="light" ref={containerRef} className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4 gsap-reveal" style={{
        opacity: 0
      }}>
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground border border-accent/30 px-3 py-1 text-xs font-semibold">
            <MapPin className="size-3 text-accent" />
            Northamptonshire SEO Insights
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight">
            Understanding the Northampton Search Landscape
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            I analyze local search behaviors across Northampton and the East Midlands to help ambitious businesses capture high-intent organic demand without agency fluff.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {marketInsights.map((insight, i) => {
          const Icon = insight.icon;
          return <Card data-index={i} key={insight.title} className="gsap-reveal bg-card border-border hover:border-accent/40 transition-colors shadow-sm flex flex-col justify-between" style={{
            opacity: 0
          }}>
                <CardHeader className="space-y-3">
                  <div className="p-2.5 w-fit rounded-lg bg-secondary text-primary">
                    <Icon className="size-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-primary font-serif">
                    {insight.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {insight.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Separator className="my-4 bg-border" />
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-accent font-mono">
                      {insight.metric}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {insight.metricLabel}
                    </span>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-muted/40 rounded-2xl p-6 sm:p-10 border border-border gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="lg:col-span-7 space-y-6">
            <Badge variant="outline" className="border-primary/20 text-primary text-xs">
              <Users className="size-3 text-primary" />
              Direct Consultant Model
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-primary">
              How I Help Northampton Businesses Win Local Market Share
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              When you work with me, you work directly with Yassine Essaadi—no junior account managers, no outsourced execution. I craft bespoke SEO strategies grounded in real Northamptonshire user search behavior to deliver measurable growth.
            </p>

            <ul className="space-y-3 pt-2">
              {strategyPoints.map((point, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-foreground">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>)}
            </ul>

            <div className="pt-4 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary">
                <Link to="/LocalSEOPage">
                  Explore Local SEO Services
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative space-y-4">
            <div className="relative rounded-xl overflow-hidden border border-border shadow-md bg-card p-2">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/northampton_seo_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/northampton_seo_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/northampton_seo_strategy.png-wvc-sizes" alt="Yassine Essaadi analyzing local search landscape and SEO strategy for Northampton businesses" className="w-full h-auto rounded-lg object-cover" loading="lazy" />
            </div>

            <Card className="bg-card/95 border-border shadow-sm p-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <BarChart3 className="size-4 text-primary" />
                  Northampton Search Intent
                </span>
                <span className="text-accent font-semibold">High Opportunity</span>
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                Targeting local commercial queries turns regional searches into direct client conversions.
              </p>
            </Card>
          </div>
        </div>

        <div className="text-center bg-card border border-border rounded-xl p-8 sm:p-10 space-y-6 shadow-sm gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="max-w-2xl mx-auto space-y-3">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-primary">
              Ready to claim top rankings in Northampton?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Let&apos;s review your current organic performance and map out a clear, actionable SEO roadmap tailored to your specific market goals.
            </p>
          </div>
          <div className="flex justify-center items-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                Claim Your Free SEO Audit
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}